import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";

import {RosterAPI} from "@/api/roster";
import {CompetitionSeasonAPI} from "@/api/competitionSeason";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	getInternalClubId,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";

import "./index.scss";

const ManagerHandleFootballTeamPage = (props) => {
	const {edit, handleNotification, source = "manager"} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const reduxSeasons = useSelector((state) => state?.seasons);

	const basePath =
		source === "manager" ? "/manager/teams" : "/development/rosters";

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});

	// Stagioni — già caricate al bootstrap in Redux, nessun re-fetch
	const seasons = (reduxSeasons || []).map((s) => ({
		value: s.id,
		label: s.name,
	}));

	// Edizioni competizione — caricate al mount, filtrate client-side per stagione selezionata
	const [allCompetitionSeasons, setAllCompetitionSeasons] = useState([]);
	const getCompetitionSeasons = async () => {
		try {
			const response = await CompetitionSeasonAPI.list();
			setAllCompetitionSeasons(
				(response || []).map((cs) => ({
					value: cs.id,
					label: (cs.competition?.name || "") + " - " + (cs.season?.name || ""),
					seasonId: cs.season?.id,
				})),
			);
		} catch {
			// silenzioso
		}
	};

	const competitionSeasonsForCurrentSeason = fields.currentSeason
		? allCompetitionSeasons.filter(
				(cs) => cs.seasonId === fields.currentSeason.value,
			)
		: [];

	useEffect(() => {
		getCompetitionSeasons();
	}, [source]); // eslint-disable-line react-hooks/exhaustive-deps

	const getRoster = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(id);
			if (response.club) {
				response.currentClub = {
					value: response.club?.id,
					label: response.club?.name,
				};
			}
			if (response.playerCategory) {
				response.currentCategory = {
					value: response.playerCategory?.id,
					label: response.playerCategory?.name,
				};
			}
			if (response.season) {
				response.currentSeason = {
					value: response.season.id,
					label: response.season.name,
				};
			}
			if (response.competitionSeasons?.length) {
				response.currentCompetitionSeasons = response.competitionSeasons.map(
					(cs) => ({
						value: cs.id,
						label:
							(cs.competition?.name || "") + " - " + (cs.season?.name || ""),
						seasonId: cs.season?.id,
					}),
				);
			}
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getRoster();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return ["name", "currentSeason"];
	};

	const saveRoster = async () => {
		try {
			const errors = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errors);

			if (!errors.length) {
				setLoading(true);

				let body = {
					...fields,
					seasonId: fields.currentSeason?.value || seasonId,
					name: fields.name || "",
					prospect: source === "scouting",
					competitionSeasons: (fields.currentCompetitionSeasons || []).map(
						(cs) => ({id: cs.value}),
					),
				};

				body.clubId = getInternalClubId();

				const response = edit
					? await RosterAPI.update(id, body)
					: await RosterAPI.create(body);

				const rosterId = response?.id || id;
				handleNotification(SUCCESSFUL_OPERATION, "success");
				navigate(`${basePath}/${rosterId}`);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveRoster();
	};

	const goBack = () => {
		if (edit) {
			navigate(basePath + "/" + id);
		} else {
			navigate(basePath);
		}
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const breadcrumbsElements = [{label: "Rose", path: basePath}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields.name || "",
				path: basePath + "/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuova rosa"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<VLFCard
					openable={false}
					title="Informazioni principali"
					content={
						<div className="row no-margin">
							<div className="col-sm-12 col-md-6 padding-small">
								{getTextField({
									...defaultTextField,
									fieldKey: "name",
									label: "Nome",
									required: true,
								})}
							</div>
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={seasons}
									sortOrder="descend"
									value={fields.currentSeason}
									onChange={(newValue) => {
										setErrors([]);
										setFields({
											...fields,
											currentSeason: newValue,
											currentCompetitionSeasons: [],
										});
									}}
									label="Stagione"
									required
									helperText={
										errors.indexOf("currentSeason") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("currentSeason") !== -1}
								/>
							</div>
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={competitionSeasonsForCurrentSeason}
									value={fields.currentCompetitionSeasons || []}
									onChange={(newValue) => {
										setFields({
											...fields,
											currentCompetitionSeasons: newValue || [],
										});
									}}
									label="Competizioni"
									isMulti
									isClearable
									isDisabled={!fields.currentSeason}
								/>
							</div>
						</div>
					}
				/>
			</div>
		</Page>
	);
};

export default ManagerHandleFootballTeamPage;
